import { listen, reactive, system } from "kho";
import { $cloudScenes } from "../data/cloud";
import { $camera, $frame, $renderer } from "../data/scene";
import { $viewer } from "../data/viewer";

/**
 * viewerAdapterSystem — the bridge from the imperative EARTHBRAIN Viewer into the
 * kho editor world. Each frame it pulls camera / renderer / point-cloud scenes
 * off `$viewer` and publishes them to the abstract atoms the gizmo + instance
 * systems react to. This replaces the imperative `setCamera/setRenderer/
 * setCloudScenes` bridge that DesignManager used to call by hand.
 *
 * It must run BEFORE the gizmo/instance systems each frame (they read $camera /
 * $renderer in their own $frame handlers), so it is added to $systems first.
 */
export const viewerAdapterSystem = system((scope) => {
  const { atoms } = scope(reactive);
  const { on } = scope(listen);

  // Only notify $cloudScenes on a real change: the container scenes keep the
  // same identity while tiles page in/out as CHILDREN. Setting the atom every
  // frame would re-run the gizmo's ~20-40 GPU surface samples per frame. Same
  // containers AND same total tile-child count => skip; a streamed tile changes
  // the count => set so the arrows re-hug the fresh terrain.
  let lastCloudSig = -1;

  on($frame, () => {
    const v = atoms.get($viewer);
    if (!v) return;

    const cam = v.getCamera();
    if (cam && atoms.get($camera) !== cam) atoms.set($camera, cam);

    const r = v.getRenderer();
    if (r && atoms.get($renderer) !== r) atoms.set($renderer, r);

    const scenes = v.getCloudScenes();
    const prev = atoms.get($cloudScenes) as unknown[];
    const sameRefs =
      prev.length === scenes.length && scenes.every((s, i) => s === prev[i]);
    const sig = scenes.reduce((n, s) => n + s.children.length, 0);
    if (!(sameRefs && sig === lastCloudSig)) {
      lastCloudSig = sig;
      atoms.set($cloudScenes, scenes);
    }
  });
});
